/**
 * The latency model: the server's `debug` frames plus local client events, grouped into
 * conversation turns so one time axis shows where a turn's seconds went.
 *
 * Written as pure reducers over an immutable state object rather than as a stateful service, so
 * the store can hold it, React can re-render off its identity, and the tests can hand it a clock
 * instead of mocking one.
 */

/** Which stage of a turn an event belongs to. Drives every colour in the panel. */
export type DebugPhase = 'voice' | 'asr' | 'llm' | 'tool' | 'tts' | 'out';

export const CLIENT_EVENTS = {
  speechStart: 'user starts speaking',
  speechEnd: 'user stops speaking',
  responseStream: 'client starts receiving response stream',
} as const;

const PHASE_BY_EVENT: Record<string, DebugPhase> = {
  [CLIENT_EVENTS.speechStart]: 'voice',
  'input voice stream start': 'voice',
  'transcription stream start': 'asr',
  [CLIENT_EVENTS.speechEnd]: 'voice',
  'input voice stream end': 'voice',
  'transcription stream end': 'asr',
  'llm request sent': 'llm',
  'llm first token received': 'llm',
  'tool call': 'tool',
  'tool result': 'tool',
  'llm response finished': 'llm',
  'tts request sent': 'tts',
  'text response stream start': 'out',
  'text response stream finished': 'out',
  'voice response stream start': 'out',
  [CLIENT_EVENTS.responseStream]: 'out',
  'voice response stream finished': 'out',
  // Our own gateway names the reply's frames without saying which of the two streams they are: it
  // releases text and speech together, so there is only one.
  'response stream start': 'out',
  'response stream finished': 'out',
};

/**
 * The events that end a turn — speech outlasts text either way — so that what follows one opens the
 * next. Only load-bearing for a gateway that names no turn, which is the one case where the panel has
 * nothing but the event order to cut turns on.
 */
const TURN_END_EVENTS = new Set(['voice response stream finished', 'response stream finished']);

const PREVIEW_LIMIT = 96;

export type EventSource = 'client' | 'server';

interface RawEvent {
  turn: number;
  description: string;
  content: string | object | undefined;
  /** Raw stamp: server epoch ms for a server frame, client epoch ms for a local event. */
  ms: number;
  source: EventSource;
}

export interface DebugEvent {
  description: string;
  phase: DebugPhase;
  source: EventSource;
  /** Stamp normalized onto the client clock. */
  atMs: number;
  /** Ms since the turn started. */
  sinceTurnMs: number;
  /** Ms since the preceding event in the turn. */
  sincePrevMs: number;
  /** Fraction of the turn's span where the previous event sat, 0–1. */
  trackStart: number;
  /** Fraction of the turn's span this event's wait occupied, 0–1. */
  trackWidth: number;
  preview: string;
  full: string;
  hasContent: boolean;
}

export interface BudgetSegment {
  phase: DebugPhase | 'gap';
  label: string;
  ms: number;
}

export interface DebugTurn {
  index: number;
  events: DebugEvent[];
  /** Mouth-close to the reply starting to speak — the number that matters. Null until both land. */
  totalMs: number | null;
  /** Named spans inside {@link totalMs}, with the unaccounted gaps between them. */
  segments: BudgetSegment[];
  /** Every labelled span, for a readout whose numbers cannot be clipped by a narrow bar. */
  stages: BudgetSegment[];
}

export interface DebugState {
  events: RawEvent[];
  /**
   * Which bucket each named turn's events go in. A turn keeps its bucket for as long as frames
   * naming it keep arriving, so a reply that was talked over goes on filing its own tail rather than
   * writing into the turn that replaced it — the two overlap, and merged into one bucket their
   * stages read as a single turn that took as long as both.
   */
  turnIndexById: Record<string, number>;
  /**
   * Client clock minus server clock, in ms. A server frame is stamped when the event happened
   * and then has to reach us, so every sample overstates the skew by that trip; the smallest
   * sample seen is the closest estimate available. No round-trip sync, so two server stamps
   * compare exactly (both shift by the same constant) and only client-to-server deltas carry
   * the residual error.
   */
  clockOffset: number | null;
  turnCount: number;
  turnOpen: boolean;
  /** Newest normalized stamp filed under the current turn. */
  turnLatestMs: number;
}

export const EMPTY_DEBUG_STATE: DebugState = {
  events: [],
  turnIndexById: {},
  clockOffset: null,
  turnCount: 0,
  turnOpen: false,
  turnLatestMs: 0,
};

/**
 * Records a `debug` frame from the server, stamped on the server's clock.
 *
 * @param turnId the turn the server said the frame belongs to, if it said
 */
export function recordServer(
  state: DebugState,
  description: string,
  content: string | object | undefined,
  timestampMs: number,
  nowMs: number,
  turnId?: string,
): DebugState {
  const sample = nowMs - timestampMs;
  const clockOffset =
    state.clockOffset === null || sample < state.clockOffset ? sample : state.clockOffset;

  return append({ ...state, clockOffset }, description, content, timestampMs, 'server', turnId);
}

/**
 * Records a local client event, stamped on the client's clock.
 *
 * @param turnId the turn the client took this action in, if it knows one
 */
export function recordClient(
  state: DebugState,
  description: string,
  nowMs: number,
  turnId?: string,
): DebugState {
  return append(state, description, undefined, nowMs, 'client', turnId);
}

/**
 * Records the first reply frame of a turn, ignoring the ones after it.
 *
 * Every text chunk and audio segment of a reply arrives through the same handler, and only the
 * first of them is the moment worth timing. Returns the state unchanged when already stamped, so
 * the caller can hand this every frame.
 *
 * @param turnId the turn the frame belongs to, if the server named one
 */
export function noteResponseStream(state: DebugState, nowMs: number, turnId?: string): DebugState {
  const turn = turnId === undefined ? state.turnCount : state.turnIndexById[turnId];
  const seen = state.events.some(
    (e) => e.turn === turn && e.description === CLIENT_EVENTS.responseStream,
  );
  if (seen) return state;

  return recordClient(state, CLIENT_EVENTS.responseStream, nowMs, turnId);
}

/**
 * Files an event under a turn.
 *
 * An event that names its turn is filed under that name, which is the only way to keep two
 * overlapping turns apart. Everything else is cut on arrival order rather than on stamp order:
 * arrival is causal, and `debug` frames are not paced against the reply, so a stamp can sort ahead of
 * the client event it actually followed. Within a turn the events are then ordered by stamp for
 * display.
 *
 * The two mix, because a gateway that names nothing still sends its diagnostics into a turn the
 * client named locally when the user pressed the microphone: a named event leaves the turn it opened
 * open, so the unnamed events that follow join it.
 */
function append(
  state: DebugState,
  description: string,
  content: string | object | undefined,
  ms: number,
  source: EventSource,
  turnId?: string,
): DebugState {
  const atMs = source === 'server' ? ms + (state.clockOffset ?? 0) : ms;

  if (turnId !== undefined) {
    const known = state.turnIndexById[turnId];
    const turn = known ?? state.turnCount + 1;
    return {
      ...state,
      turnCount: Math.max(state.turnCount, turn),
      // A turn is left open by its own events however it ended, since what closes it is the next
      // turn being named — not a terminal event this one may never send, an abandoned turn being
      // precisely the turn that does not send it.
      turnOpen: true,
      turnIndexById:
        known === undefined ? { ...state.turnIndexById, [turnId]: turn } : state.turnIndexById,
      turnLatestMs: known === undefined ? atMs : Math.max(state.turnLatestMs, atMs),
      events: [...state.events, { turn, description, content, ms, source }],
    };
  }

  const rejoining = !state.turnOpen && belongsToClosedTurn(state, source, atMs);

  let turnCount = state.turnCount;
  let turnLatestMs = state.turnLatestMs;

  if (description === CLIENT_EVENTS.speechStart || (!state.turnOpen && !rejoining)) {
    turnCount++;
    turnLatestMs = 0;
  }

  // A rejoining frame must not reopen the turn, or the next one would be swallowed by it.
  const turnOpen = rejoining ? state.turnOpen : !TURN_END_EVENTS.has(description.toLowerCase());

  return {
    ...state,
    turnCount,
    turnOpen,
    turnLatestMs: Math.max(turnLatestMs, atMs),
    events: [...state.events, { turn: turnCount, description, content, ms, source }],
  };
}

/**
 * Whether a frame arriving after the end marker was in fact stamped inside the turn that marker
 * closed. Diagnostics are not paced but the reply is, so the marker can overtake a frame that
 * happened before it — which must not be read as the start of a new turn.
 */
function belongsToClosedTurn(state: DebugState, source: EventSource, atMs: number): boolean {
  return source === 'server' && state.turnCount > 0 && atMs < state.turnLatestMs;
}

export function buildTurns(state: DebugState): DebugTurn[] {
  const offset = state.clockOffset ?? 0;
  const byTurn = new Map<number, DebugEvent[]>();

  for (const raw of state.events) {
    const atMs = raw.source === 'server' ? raw.ms + offset : raw.ms;
    const { preview, full } = stringify(raw.content);

    const event: DebugEvent = {
      description: raw.description,
      // Lowercased: the server's own capitalization of these labels is not stable.
      phase: PHASE_BY_EVENT[raw.description.toLowerCase()] ?? 'out',
      source: raw.source,
      atMs,
      sinceTurnMs: 0,
      sincePrevMs: 0,
      trackStart: 0,
      trackWidth: 0,
      preview,
      full,
      hasContent: full !== '',
    };

    const bucket = byTurn.get(raw.turn);
    if (bucket) bucket.push(event);
    else byTurn.set(raw.turn, [event]);
  }

  return [...byTurn.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([index, events]) => buildTurn(index, events));
}

function buildTurn(index: number, unsorted: DebugEvent[]): DebugTurn {
  const events = [...unsorted].sort((a, b) => a.atMs - b.atMs);
  const start = events[0].atMs;
  const span = Math.max(events[events.length - 1].atMs - start, 1);

  events.forEach((event, i) => {
    const prev = i === 0 ? start : events[i - 1].atMs;
    event.sinceTurnMs = event.atMs - start;
    event.sincePrevMs = event.atMs - prev;
    event.trackStart = (prev - start) / span;
    event.trackWidth = (event.atMs - prev) / span;
  });

  const { totalMs, segments } = buildBudget(events);
  const stages = segments.filter((seg) => seg.label !== '');
  return { index, events, totalMs, segments, stages };
}

/**
 * Splits mouth-close-to-first-frame into the stages we tune — recognition, the model, and speech
 * synthesis — leaving whatever they do not account for as an explicit gap, so the segments always
 * add up to the total rather than quietly hiding time.
 */
function buildBudget(events: DebugEvent[]): { totalMs: number | null; segments: BudgetSegment[] } {
  const at = (description: string) =>
    events.find((e) => e.description.toLowerCase() === description.toLowerCase())?.atMs ?? null;

  // A typed turn never opens the microphone, so the request going out is its start.
  const from =
    at(CLIENT_EVENTS.speechEnd) ?? at('input voice stream end') ?? at('llm request sent');
  // Speech starting is the end of the wait worth measuring. Text is released ahead of it, as soon as
  // the model produces it — but the reply is not answered until it is spoken.
  const to =
    at('voice response stream start') ??
    at(CLIENT_EVENTS.responseStream) ??
    at('text response stream start') ??
    at('response stream start');
  if (from === null || to === null || to <= from) return { totalMs: null, segments: [] };

  const named: {
    phase: DebugPhase | 'gap';
    label: string;
    from: number | null;
    to: number | null;
  }[] = [
    { phase: 'asr', label: 'asr', from, to: at('transcription stream end') },
    {
      phase: 'llm',
      label: 'llm ttft',
      from: at('llm request sent'),
      to: at('llm first token received'),
    },
    // Synthesis is requested a sentence at a time, so the first request going out is the model
    // having streamed until a *sentence* is speakable — not until the reply is done.
    {
      phase: 'gap',
      label: 'llm 1st sentence',
      from: at('llm first token received'),
      to: at('tts request sent'),
    },
    {
      phase: 'tts',
      label: 'tts',
      from: at('tts request sent'),
      to: at('voice response stream start'),
    },
  ];

  const segments: BudgetSegment[] = [];
  let cursor = from;

  for (const span of named) {
    if (span.from === null || span.to === null) continue;

    const spanFrom = Math.max(span.from, cursor);
    const spanTo = Math.min(span.to, to);
    if (spanTo <= spanFrom) continue;

    if (spanFrom > cursor) segments.push({ phase: 'gap', label: '', ms: spanFrom - cursor });
    segments.push({ phase: span.phase, label: span.label, ms: spanTo - spanFrom });
    cursor = spanTo;
  }

  if (to > cursor) segments.push({ phase: 'gap', label: '', ms: to - cursor });

  return { totalMs: to - from, segments };
}

function stringify(content: string | object | undefined): { preview: string; full: string } {
  if (content === undefined || content === null || content === '') return { preview: '', full: '' };

  const full = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
  const flat = full.replace(/\s+/g, ' ').trim();
  const preview = flat.length > PREVIEW_LIMIT ? `${flat.slice(0, PREVIEW_LIMIT)}…` : flat;
  return { preview, full };
}
